// Libraries
import { assign, createMachine } from "xstate";
// API
import { UploadCsv_uploadCsv } from "api/Import/CustomImport/types/UploadCsv";
import { GetActivityTypes_activityTypes } from "api/MyLivestock/Activity/types/GetActivityTypes";
import { GetActivityTemplates_activityTemplates } from "api/MyLivestock/Activity/types/GetActivityTemplates";
import { GetAnimalTypes_animalTypes } from "api/MyLivestock/Animal/types/GetAnimalTypes";
// State
import { Species } from "state";

// Context
export type ImportCustomWizardAnimalType = Species | GetAnimalTypes_animalTypes;
interface ImportCustomWizardContext {
  activityTemplate?: GetActivityTemplates_activityTemplates;
  activityType?: GetActivityTypes_activityTypes;
  animalType?: ImportCustomWizardAnimalType;
  stageNumber: 1 | 2 | 3 | 4 | 5;
  subtitle: string;
  taskId?: string;
  title: string;
  uploadedFile?: UploadCsv_uploadCsv;
  uploadedFileName?: string;
}

// Events
interface SelectActivityTypeEvent {
  type: "SELECT_ACTIVITY_TYPE";
  activityType: GetActivityTypes_activityTypes;
}

interface ResetActivityTypeEvent {
  type: "RESET_ACTIVITY_TYPE";
}

interface SelectAnimalTypeEvent {
  type: "SELECT_ANIMAL_TYPE";
  animalType: ImportCustomWizardAnimalType;
}

interface ResetAnimalTypeEvent {
  type: "RESET_ANIMAL_TYPE";
}

interface SelectActivityTemplateEvent {
  type: "SELECT_ACTIVITY_TEMPLATE";
  activityTemplate: GetActivityTemplates_activityTemplates;
}

interface ResetActivityTemplateEvent {
  type: "RESET_ACTIVITY_TEMPLATE";
}

interface SetUploadedFileEvent {
  type: "SET_UPLOADED_FILE";
  uploadedFile: UploadCsv_uploadCsv;
  uploadedFileName: string;
}

interface ProceedWithUploadedFileEvent {
  type: "PROCEED_WITH_UPLOADED_FILE";
}

interface ResetUploadedFileEvent {
  type: "RESET_UPLOADED_FILE";
}

interface SetTaskIdEvent {
  type: "SET_TASK_ID";
  taskId: string;
}

interface RestartEvent {
  type: "RESTART";
}

interface SetTaskSuccessEvent {
  type: "SET_TASK_SUCCESS";
}

type ImportCustomWizardEvents =
  | SelectActivityTypeEvent
  | ResetActivityTypeEvent
  | SelectAnimalTypeEvent
  | ResetAnimalTypeEvent
  | SelectActivityTemplateEvent
  | ResetActivityTemplateEvent
  | SetUploadedFileEvent
  | ProceedWithUploadedFileEvent
  | ResetUploadedFileEvent
  | SetTaskIdEvent
  | RestartEvent
  | SetTaskSuccessEvent;

const initialContext: ImportCustomWizardContext = {
  activityTemplate: undefined,
  activityType: undefined,
  animalType: undefined,
  stageNumber: 1,
  subtitle: "Step 1: Choose activity type",
  taskId: undefined,
  title: "Choose type",
  uploadedFile: undefined,
  uploadedFileName: undefined,
};

export const importCustomWizardMachine = createMachine<ImportCustomWizardContext, ImportCustomWizardEvents>(
  {
    id: "importCustom",
    context: initialContext,
    initial: "chooseType",
    states: {
      chooseType: {
        entry: assign((_context) => initialContext),
        on: {
          SELECT_ACTIVITY_TYPE: {
            target: "chooseTemplate",
            actions: ["selectActivityType"],
          },
          SELECT_ANIMAL_TYPE: {
            target: "chooseFile",
            actions: ["selectAnimalType"],
          },
        },
      },
      chooseTemplate: {
        entry: assign((context) => {
          if (!context.activityType) return {};

          return {
            title: context.activityType.name,
            stageNumber: 2,
            subtitle: `Step 2: Choose "${context.activityType.name}" type`,
          };
        }),
        on: {
          RESET_ACTIVITY_TYPE: {
            target: "chooseType",
            actions: ["resetActivityType"],
          },
          SELECT_ACTIVITY_TEMPLATE: {
            target: "chooseFile",
            actions: ["selectActivityTemplate"],
          },
        },
      },
      chooseFile: {
        entry: assign((context) => {
          if (context.activityTemplate) {
            return {
              title: `Upload file: ${context.activityTemplate.name}`,
              stageNumber: 3,
              subtitle: `Step 3: Choose your "${context.activityTemplate.name}" file to import`,
            };
          }

          if (context.animalType) {
            return {
              title: `Upload file: ${context.animalType.name}`,
              stageNumber: 3,
              subtitle: `Step 3: Choose your "${context.animalType.name}" file to import`,
            };
          }

          return {};
        }),
        on: {
          RESET_ANIMAL_TYPE: {
            target: "chooseType",
            actions: ["resetAnimalType"],
          },
          RESET_ACTIVITY_TEMPLATE: {
            target: "chooseTemplate",
            actions: ["resetActivityTemplate"],
          },
          SET_UPLOADED_FILE: {
            actions: ["setUploadedFile"],
          },
          RESET_UPLOADED_FILE: {
            actions: ["resetUploadedFile"],
          },
          PROCEED_WITH_UPLOADED_FILE: {
            target: "chooseColumns",
          },
        },
      },
      chooseColumns: {
        entry: assign((_context) => ({
          title: "Match and import",
          stageNumber: 4,
          subtitle: "Step 4: Match the fields to the columns in your file",
        })),
        on: {
          RESTART: {
            target: "chooseType",
          },
          RESET_UPLOADED_FILE: {
            target: "chooseFile",
            actions: ["resetUploadedFile"],
          },
          SELECT_ACTIVITY_TEMPLATE: {
            actions: ["selectActivityTemplate"],
          },
          SET_TASK_ID: {
            target: "result",
            actions: ["setTaskId"],
          },
        },
      },
      result: {
        entry: assign((_context) => ({
          title: "Import in progress",
          stageNumber: 4,
          subtitle: "We're working on it",
        })),
        on: {
          RESTART: {
            target: "chooseType",
          },
          SET_TASK_SUCCESS: {
            actions: ["setTaskSuccess"],
          },
        },
      },
    },
  },
  {
    actions: {
      selectActivityType: assign((_context, event) => {
        if (event.type !== "SELECT_ACTIVITY_TYPE") return {};

        return {
          activityType: event.activityType,
        };
      }),
      resetActivityType: assign((_context) => ({
        activityType: undefined,
      })),
      selectAnimalType: assign((_context, event) => {
        if (event.type !== "SELECT_ANIMAL_TYPE") return {};

        return {
          animalType: event.animalType,
        };
      }),
      resetAnimalType: assign((_context) => ({
        animalType: undefined,
      })),
      selectActivityTemplate: assign((_context, event) => {
        if (event.type !== "SELECT_ACTIVITY_TEMPLATE") return {};

        return {
          activityTemplate: event.activityTemplate,
          activityType: event.activityTemplate.activityType || undefined,
        };
      }),
      resetActivityTemplate: assign((_context) => ({
        activityTemplate: undefined,
      })),
      setUploadedFile: assign((_context, event) => {
        if (event.type !== "SET_UPLOADED_FILE") return {};

        return {
          uploadedFile: event.uploadedFile,
          uploadedFileName: event.uploadedFileName,
        };
      }),
      resetUploadedFile: assign((_context) => ({
        uploadedFile: undefined,
      })),
      setTaskId: assign((_context, event) => {
        if (event.type !== "SET_TASK_ID") return {};

        return {
          taskId: event.taskId,
        };
      }),
      setTaskSuccess: assign((context) => {
        const subtitle = context.activityTemplate
          ? `You have successfully imported your ${context.activityTemplate.name} file`
          : context.animalType
          ? `You have successfully imported your ${context.animalType.name} file`
          : "You have successfully imported your file";

        return {
          title: "Successfully imported",
          stageNumber: 5,
          subtitle,
        };
      }),
    },
  },
);
